import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
  MDBCardText,
  MDBIcon,
  MDBTypography,
  MDBBtn
} from 'mdb-react-ui-kit';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import './Home.css';

interface HomeProps {
  theme: 'light' | 'dark';
}

const Home: React.FC<HomeProps> = ({ theme }) => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [stats, setStats] = useState({
    totalSessions: 0,
    totalShots: 0,
    avgScore: 0,
  });

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadStats();
    }
  }, [authLoading, isAuthenticated]);

  // Show generic landing page for non-authenticated users
  if (!isAuthenticated) {
    return (
      <MDBContainer className="py-5">
        <MDBRow className="mb-5">
          <MDBCol>
            <div className="text-center mb-5">
              <MDBTypography tag='h1' className='display-3 fw-bold mb-3'>
                Welcome to SolidRange
              </MDBTypography>
              <MDBTypography tag='p' className='lead text-muted mb-4'>
                Transform your shooting practice with intelligent tracking and analysis. 
                Automatically detect shots, score accuracy, and track your progress.
              </MDBTypography>
              <MDBBtn color='success' size='lg' onClick={() => navigate('/login')}>
                <MDBIcon fas icon='sign-in-alt' className='me-2' /> Sign In to Get Started
              </MDBBtn>
            </div>
          </MDBCol>
        </MDBRow>

        <MDBRow className="mb-5">
          <MDBCol>
            <MDBTypography tag='h2' className='h3 mb-4 text-center'>
              <MDBIcon fas icon="star" className="text-warning me-2" /> Key Features
            </MDBTypography>
          </MDBCol>
        </MDBRow>

        <MDBRow className="g-4 mb-5">
          <MDBCol md="6" lg="4">
            <MDBCard className="h-100 shadow-sm feature-card text-center">
              <MDBCardBody>
                <div className="mb-3">
                  <MDBIcon fas icon="camera" size="3x" className="text-primary" />
                </div>
                <MDBCardTitle className="h5">Manual Entry</MDBCardTitle>
                <MDBCardText className="text-muted">
                  Upload target images and manually enter shot data for comprehensive tracking
                </MDBCardText>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>

          <MDBCol md="6" lg="4">
            <MDBCard className="h-100 shadow-sm feature-card text-center">
              <MDBCardBody>
                <div className="mb-3">
                  <MDBIcon fas icon="bullseye" size="3x" className="text-danger" />
                </div>
                <MDBCardTitle className="h5">Visual Target View</MDBCardTitle>
                <MDBCardText className="text-muted">
                  See all your shots on an interactive target display with color-coded rings
                </MDBCardText>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>

          <MDBCol md="6" lg="4">
            <MDBCard className="h-100 shadow-sm feature-card text-center">
              <MDBCardBody>
                <div className="mb-3">
                  <MDBIcon fas icon="save" size="3x" className="text-info" />
                </div>
                <MDBCardTitle className="h5">Session Storage</MDBCardTitle>
                <MDBCardText className="text-muted">
                  Save your shooting sessions and review them anytime across all your devices
                </MDBCardText>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>

          <MDBCol md="6" lg="4">
            <MDBCard className="h-100 shadow-sm feature-card text-center">
              <MDBCardBody>
                <div className="mb-3">
                  <MDBIcon fas icon="cloud" size="3x" className="text-info" />
                </div>
                <MDBCardTitle className="h5">Cloud Sync</MDBCardTitle>
                <MDBCardText className="text-muted">
                  Access your data from anywhere - web, mobile, or tablet with automatic sync
                </MDBCardText>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>

        <MDBRow>
          <MDBCol>
            <MDBCard className="bg-success text-white shadow-lg">
              <MDBCardBody className="text-center p-5">
                <MDBTypography tag='h2' className='h3 mb-3'>Ready to Improve Your Shooting?</MDBTypography>
                <MDBCardText className="mb-4">
                  Sign in with your Google account to start tracking your shooting sessions and improving your accuracy.
                </MDBCardText>
                <MDBBtn color='light' size='lg' onClick={() => navigate('/login')}>
                  <MDBIcon fas icon='sign-in-alt' className='me-2' /> Get Started Now
                </MDBBtn>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>

        <MDBRow className="mt-5">
          <MDBCol className="text-center">
            <p className="text-muted small">
              <Link to="/privacy-policy" className="text-muted text-decoration-none me-3">Privacy Policy</Link>
              <Link to="/terms-of-service" className="text-muted text-decoration-none">Terms of Service</Link>
            </p>
          </MDBCol>
        </MDBRow>
      </MDBContainer>
    );
  }

  const loadStats = async () => {
    try {
      const sessions = await apiService.getAllSessions();
      const totalSessions = sessions.length;
      const totalShots = sessions.reduce((sum, s) => sum + s.shotCount, 0);
      const avgScore = sessions.length > 0
        ? sessions.reduce((sum, s) => sum + s.totalScore, 0) / sessions.reduce((sum, s) => sum + s.shotCount, 0)
        : 0;

      setStats({ totalSessions, totalShots, avgScore });
    } catch (error) {
      // Keep stats at 0 if fetch fails
    }
  };

  return (
    <MDBContainer className="py-4">
      <MDBRow className="mb-4">
        <MDBCol>
          <div className="text-center mb-4">
            <MDBTypography tag='h1' className='display-5 fw-bold mb-3'>
              Welcome to SolidRange!
            </MDBTypography>
            <MDBTypography tag='p' className='lead text-muted'>
              Transform your shooting practice with intelligent tracking and analysis. SolidRange 
              automatically detects each shot, scores your accuracy, and saves your progress - 
              helping you become a better shooter with every session.
            </MDBTypography>
          </div>
        </MDBCol>
      </MDBRow>

      <MDBRow className="g-3 mb-4">
        <MDBCol md="4">
          <MDBCard className="text-center shadow-sm h-100">
            <MDBCardBody>
              <MDBTypography tag='h2' className='display-4 text-primary fw-bold'>{stats.totalSessions}</MDBTypography>
              <MDBTypography tag='h6' className='text-muted text-uppercase'>Total Sessions</MDBTypography>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
        <MDBCol md="4">
          <MDBCard className="text-center shadow-sm h-100">
            <MDBCardBody>
              <MDBTypography tag='h2' className='display-4 text-success fw-bold'>{stats.totalShots}</MDBTypography>
              <MDBTypography tag='h6' className='text-muted text-uppercase'>Total Shots</MDBTypography>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
        <MDBCol md="4">
          <MDBCard className="text-center shadow-sm h-100">
            <MDBCardBody>
              <MDBTypography tag='h2' className='display-4 text-warning fw-bold'>{stats.avgScore.toFixed(1)}</MDBTypography>
              <MDBTypography tag='h6' className='text-muted text-uppercase'>Avg Score</MDBTypography>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>

      <MDBRow className="mb-4">
        <MDBCol>
          <MDBTypography tag='h2' className='h4 mb-3 text-center'>
            <MDBIcon fas icon="star" className="text-warning me-2" /> Key Features
          </MDBTypography>
        </MDBCol>
      </MDBRow>

      <MDBRow className="g-3 mb-4">
        <MDBCol md="6" lg="4">
          <MDBCard className="h-100 shadow-sm feature-card">
            <MDBCardBody>
              <div className="text-center mb-3">
                <MDBIcon fas icon="camera" size="3x" className="text-primary" />
              </div>
              <MDBCardTitle className="h5 text-center">Manual Entry</MDBCardTitle>
              <MDBCardText className="text-muted">
                Upload target images and manually enter shot data for comprehensive tracking
              </MDBCardText>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>

        <MDBCol md="6" lg="4">
          <MDBCard className="h-100 shadow-sm feature-card">
            <MDBCardBody>
              <div className="text-center mb-3">
                <MDBIcon fas icon="bullseye" size="3x" className="text-danger" />
              </div>
              <MDBCardTitle className="h5 text-center">Visual Target View</MDBCardTitle>
              <MDBCardText className="text-muted">
                See all your shots on an interactive target display with color-coded rings
              </MDBCardText>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>

        <MDBCol md="6" lg="4">
          <MDBCard className="h-100 shadow-sm feature-card">
            <MDBCardBody>
              <div className="text-center mb-3">
                <MDBIcon fas icon="save" size="3x" className="text-info" />
              </div>
              <MDBCardTitle className="h5 text-center">Session Storage</MDBCardTitle>
              <MDBCardText className="text-muted">
                Save your shooting sessions and review them anytime in the Sessions page
              </MDBCardText>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>

      <MDBRow>
        <MDBCol>
          <MDBCard className="bg-primary text-white shadow">
            <MDBCardBody className="text-center p-4">
              <MDBTypography tag='h2' className='h4 mb-3'>Ready to Start?</MDBTypography>
              <MDBCardText>
                Head over to the <strong>Range</strong> page to add a new shooting session. 
                When you're done, save your session and review it anytime in the <strong>Sessions</strong> page!
              </MDBCardText>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>
    </MDBContainer>
  );
};

export default Home;
